import {
  getEvaluableOrPlaceholder,
  getEvaluatorShortname,
  getUserShortname,
} from "@engine/round/accessors";
import { type RoundAggregate } from "@engine/round/aggregate";
import { type StampableAction } from "@engine/round/actions";
import { FINALITY_LABELS } from "@lib/mvfFinality";

// Returns a short, human-readable description of an action payload, suitable
// for surfaces like undo/redo toasts where raw JSON or the SCREAMING_SNAKE
// action type would be jarring. Falls back to a sentence-cased version of the
// action type for actions we haven't called out explicitly.
export function humanizeActionPayload(
  round: RoundAggregate,
  payload: StampableAction,
): string {
  switch (payload.type) {
    case "EVALUATION_MVF_CHANGE":
      return payload.setZero
        ? `Set ${evaluableName(round, payload.evaluableId)} MVF to zero`
        : `Edited MVF for ${evaluableName(round, payload.evaluableId)}`;
    case "EVALUATION_SET_ZERO":
      return `Set ${evaluableName(round, payload.evaluableId)} MVF to zero`;
    case "EVALUATION_FINALITY_CHANGE":
      return payload.finality
        ? `Marked ${evaluableName(round, payload.evaluableId)} as "${FINALITY_LABELS[payload.finality].short}"`
        : `Cleared final verdict on ${evaluableName(round, payload.evaluableId)}`;
    case "EVALUATION_NEGATIVE_VALUE_CHANGE":
      return payload.negativeValue
        ? `Flagged ${evaluableName(round, payload.evaluableId)} as negative value`
        : `Removed the negative-value flag on ${evaluableName(round, payload.evaluableId)}`;
    case "MATCHING_MVF_CHANGE":
      return `Edited matching MVF for ${evaluableName(round, payload.evaluableId)}`;
    case "NORMALIZE_EVALUATIONS":
      return "Normalized MVFs";
    case "EVALUATION_NOTES_CHANGE":
      return `Edited notes for ${evaluableName(round, payload.evaluableId)}`;
    case "EVALUATION_FEEDBACK_CHANGE":
      return `Edited feedback for ${evaluableName(round, payload.evaluableId)}`;
    case "NOTES_AND_FEEDBACK_VISIBILITY_CHANGE":
      return `Changed notes & feedback visibility for ${evaluableName(round, payload.evaluableId)}`;
    case "EVALUATION_STATE_CHANGE":
      return describeEvaluationStateChange(round, payload);
    case "EVALUATION_SET_HIDDEN":
      return `${payload.hidden ? "Hid" : "Unhid"} ${evaluableName(round, payload.evaluableId)}`;
    case "START_EVALUATION":
      return `Started evaluation of ${evaluableName(round, payload.evaluableId)}`;
    case "INVESTIGATION_INTEREST_CHANGE":
      return `Updated investigation interest for ${evaluableName(round, payload.proposalId)}`;
    case "REVIEW_PROGRESS_CHANGE":
      return `Updated review progress for ${evaluableName(round, payload.proposalId)}`;
    case "FUNDER_RATING_CHANGE":
      return `Updated funder rating for ${getEvaluatorShortname(round, payload.recommenderId)} on ${evaluableName(round, payload.proposalId)}`;
    case "FUNDER_RATINGS_CLEAR":
      return "Cleared all funder ratings";
    case "CLEAR_FUNDER_RATINGS":
      return `Cleared funder ratings for ${getEvaluatorShortname(round, payload.funderId)}`;
    case "FUNDER_PROPOSAL_WEIGHT_CHANGE":
      return `Updated overall score for ${evaluableName(round, payload.proposalId)}`;
    case "FUNDER_PROPOSAL_EXCLUSION_TOGGLE":
      return payload.excluded
        ? `Excluded ${evaluableName(round, payload.proposalId)} from funding`
        : `Re-included ${evaluableName(round, payload.proposalId)} in funding`;
    case "FUNDER_RECOMMENDER_WEIGHT_CHANGE":
      return `Updated overall score for ${getEvaluatorShortname(round, payload.recommenderId)}`;
    case "FUNDER_BUDGET_CHANGE":
      return "Updated funder budget";
    case "FUNDER_SETTLEMENT_BUDGET_CHANGE":
      return "Updated funder settlement budget";
    case "RESET_FUNDER_INCREMENTAL_EVALS":
      return "Reset funder allocations";
    case "EVALUATOR_PROPERTY_CHANGE":
      return `Renamed ${getEvaluatorShortname(round, payload.evaluatorId)}`;
    case "ROUND_MEMBER_PROPERTY_CHANGE":
      return `Updated round membership for ${getUserShortname(round, payload.memberId)}`;
    case "ADMIN_CHANGE_ROUND_MEMBER_PROPERTY":
      return `Updated round membership for ${getUserShortname(round, payload.memberId)}`;
    case "PROPOSAL_DISCLOSURE_CHANGE":
      return `Updated disclosure for ${evaluableName(round, payload.proposalId)}`;
    case "RECUSAL_VOTE":
      return `Voted on recusal for ${evaluableName(round, payload.proposalId)}`;
    case "PROPOSAL_LONGNAME_CHANGE":
    case "PROPOSAL_SHORTNAME_CHANGE":
      return `Renamed proposal ${evaluableName(round, payload.proposalId)}`;
    case "PROPOSAL_FIELD_RESPONSE_CHANGE":
      return `Edited proposal response for ${evaluableName(round, payload.proposalId)}`;
    default:
      return sentenceCaseActionType(payload.type);
  }
}

function evaluableName(round: RoundAggregate, evaluableId: string): string {
  return getEvaluableOrPlaceholder(round, evaluableId).shortname;
}

function describeEvaluationStateChange(
  round: RoundAggregate,
  payload: Extract<StampableAction, { type: "EVALUATION_STATE_CHANGE" }>,
): string {
  const name = evaluableName(round, payload.evaluableId);
  switch (payload.change) {
    case "start":
      return `Started evaluation of ${name}`;
    case "cancel":
      return `Canceled evaluation of ${name}`;
    case "recuse":
      return `Recused from ${name}`;
    case "defer":
      return `Deferred evaluation of ${name}`;
    default:
      return `Updated evaluation state for ${name}`;
  }
}

export function sentenceCaseActionType(type: string): string {
  const lower = type.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
